import { Moment } from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import { Button, ButtonGroup } from 'components/atoms/button';
import { Radio } from 'components/atoms/radio';
import { RadioGroup as RadioGroupAtom } from 'components/atoms/radio-buttons';
import {
  Drawer,
  DrawerContent,
} from 'components/organisms/timesheets/common/drawer';
import { TimesheetAdhocTask } from '../../types';
import { Content } from '../components';
import { ActivitiesSubmittedLastWeek } from './activities-submitted-last-week';
import { NewActivityForm } from './new-activity-form';
import { AdhocTask, useAddAdhocActions, View } from './use-add-adhoc-actions';

const RadioGroup = styled(RadioGroupAtom)`
  margin: var(--spacing-06) var(--spacing-05);
`;
const FormContainer = styled.div`
  padding: var(--spacing-05);
`;

export const AddAdHocDrawer: FC<{
  visible: boolean;
  startDate: Moment;
  projects: Project[];
  timeEntryCategories: Category[];
  alreadyAddedAdhocTasks: TimesheetAdhocTask[];
  onAdhocTasksAdd: (adhocTasks: AdhocTask[]) => void;
  onClose: () => void;
}> = ({
  visible,
  startDate,
  projects,
  timeEntryCategories,
  alreadyAddedAdhocTasks,
  onAdhocTasksAdd,
  onClose,
}) => {
  const {
    projectOptions,
    categoryOptions,
    form,
    isAddDisabled,
    isBillableFieldDisabled,
    onFormValueChange,
    onAdd,
    resetValues,
    selectedView,
    setSelectedView,
    searchText,
    setSearchText,
    isLoading,
    taskList,
    selectedAllDisplayingTasks,
    onTaskSelected,
    selectedTasks,
    categoryMandatory,
  } = useAddAdhocActions({
    startDate,
    projects,
    timeEntryCategories,
    alreadyAddedAdhocTasks,
    onAdhocTasksAdd,
  });

  return (
    <Drawer
      visible={visible}
      afterVisibleChange={(_visible) => {
        if (!_visible) {
          resetValues();
          setSelectedView(View.NewActivity);
        }
      }}
      onClose={onClose}
      destroyOnClose
    >
      <Content>
        <DrawerContent.Title>
          <FormattedMessage id="timesheets.title.adhoc_activity" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <RadioGroup
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value)}
          >
            <Radio value={View.NewActivity}>
              <FormattedMessage id="timesheets.adhoc_activity.new_activity" />
            </Radio>
            <Radio value={View.SubmittedLastWeek}>
              <FormattedMessage id="timesheets.tasks.filters.submitted_last_week" />
            </Radio>
          </RadioGroup>
          {selectedView === View.NewActivity ? (
            <FormContainer>
              <NewActivityForm
                form={form}
                onFormValueChange={onFormValueChange}
                projectOptions={projectOptions}
                categoryOptions={categoryOptions}
                isBillableFieldDisabled={isBillableFieldDisabled}
                categoryMandatory={categoryMandatory}
              />
            </FormContainer>
          ) : (
            <ActivitiesSubmittedLastWeek
              searchText={searchText}
              setSearchText={setSearchText}
              loading={isLoading}
              tasks={taskList}
              selectAllTasks={selectedAllDisplayingTasks}
              onTaskSelect={onTaskSelected}
            />
          )}
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <ButtonGroup $reversed>
            {selectedView === View.NewActivity ? (
              <Button
                disabled={isAddDisabled}
                iconPlacement="right"
                type="primary"
                onClick={onAdd}
                data-cy="buttons.adhoc-drawer.add"
              >
                <FormattedMessage id="timesheets.buttons.add_adhoc_activity" />
              </Button>
            ) : (
              <Button
                disabled={selectedTasks.length === 0}
                iconPlacement="right"
                type="primary"
                onClick={onAdd}
              >
                <FormattedMessage
                  id="timesheets.buttons.add_adhoc_activity_with_count"
                  values={{
                    count: selectedTasks.length,
                  }}
                />
              </Button>
            )}
            <Button onClick={onClose} data-cy="buttons.adhoc-drawer.cancel">
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        </DrawerContent.Footer>
      </Content>
    </Drawer>
  );
};
